import { AlertSeverity, EndpointPlatform } from './enums.js';
export const RED_FLAG_CATALOG = [
    {
        code: 'RF-001',
        name: 'New Admin Added',
        nameFr: 'Nouvel administrateur ajouté',
        description: "Ajout d'un compte à un groupe d'administration (Administrateurs locaux, Domain Admins, Enterprise Admins) ou création d'un compte immédiatement privilégié. Événements Windows 4732, 4728, 4756, 4720.",
        clientExplanation: "Quelqu'un vient de donner les pleins pouvoirs à un compte sur cette machine ou sur le domaine. Si cet ajout n'était pas prévu, un attaquant est probablement déjà en train de s'installer durablement.",
        severity: AlertSeverity.CRITICAL,
        wazuhLevel: 14,
        wazuhRuleIds: ['100201'],
        trigger: 'Event 4732 / 4728 / 4756 / 4720 sur un groupe d’administration',
        sigmaId: 'win_security_admin_group_member_added.yml',
        platforms: [EndpointPlatform.WINDOWS],
        mitre: {
            tactics: ['TA0003', 'TA0004'],
            techniques: ['T1078.002', 'T1098'],
        },
        reactionPlan: [
            {
                order: 1,
                instruction: "Vérifier si l'ajout au groupe admin était planifié avec le responsable IT.",
            },
            {
                order: 2,
                instruction: 'Identifier le compte source et sa date de création.',
                command: 'net user {{user}} /domain',
                shell: 'cmd',
            },
            {
                order: 3,
                instruction: 'Vérifier les dernières connexions de ce compte.',
                command: 'Get-EventLog -LogName Security -InstanceId 4624 -Newest 20',
                shell: 'powershell',
            },
            {
                order: 4,
                instruction: "Si l'ajout n'est pas légitime : retirer immédiatement le compte du groupe d'administration et réinitialiser son mot de passe.",
                command: 'Remove-LocalGroupMember -Group "Administrateurs" -Member "{{user}}"; net user {{user}} /random',
                shell: 'powershell',
                destructive: true,
            },
            {
                order: 5,
                instruction: "Documenter l'incident et clôturer l'alerte.",
            },
        ],
    },
    {
        code: 'RF-002',
        name: 'Logs Cleared',
        nameFr: "Journaux d'audit effacés",
        description: "Effacement du journal de sécurité (Event 1102) ou du journal système (Event 104). Action typique d'un attaquant cherchant à supprimer ses traces après compromission.",
        clientExplanation: "L'historique de sécurité de cette machine vient d'être effacé. Une opération de maintenance peut l'expliquer, mais dans la majorité des cas c'est le signe qu'un attaquant efface ses traces.",
        severity: AlertSeverity.CRITICAL,
        wazuhLevel: 14,
        wazuhRuleIds: ['100202'],
        trigger: 'Event 1102 (Security) ou 104 (System)',
        sigmaId: 'win_security_eventlog_cleared.yml',
        platforms: [EndpointPlatform.WINDOWS],
        mitre: {
            tactics: ['TA0005'],
            techniques: ['T1070.001'],
        },
        reactionPlan: [
            {
                order: 1,
                instruction: "Considérer la machine comme potentiellement compromise : ne pas l'éteindre, la mémoire vive contient des preuves.",
            },
            {
                order: 2,
                instruction: "Identifier le compte à l'origine de l'effacement dans le détail de l'alerte, puis vérifier s'il correspond à une intervention planifiée.",
            },
            {
                order: 3,
                instruction: "Isoler la machine du réseau si aucune intervention n'était prévue (débrancher le câble réseau, désactiver le Wi-Fi).",
                destructive: true,
            },
            {
                order: 4,
                instruction: 'Vérifier que les journaux sont bien de nouveau alimentés et que la taille maximale du journal est suffisante.',
                command: 'wevtutil gl Security',
                shell: 'cmd',
            },
            {
                order: 5,
                instruction: "Contacter MiniSOC pour une levée de doute approfondie : les logs restant sur le nœud Proxmox couvrent la période effacée sur le poste.",
            },
        ],
    },
    {
        code: 'RF-003',
        name: 'Login Outside Working Hours',
        nameFr: 'Connexion hors heures ouvrées',
        description: "Connexion interactive réussie (Event 4624 type 2/10, ou 4648) en dehors de la plage horaire déclarée pour le tenant. Détection contextuelle, à corréler avec l'activité métier.",
        clientExplanation: "Un utilisateur s'est connecté en dehors des horaires habituels de votre organisation. Ce n'est pas forcément malveillant, mais c'est à confirmer avec la personne concernée.",
        severity: AlertSeverity.HIGH,
        wazuhLevel: 12,
        wazuhRuleIds: ['100203'],
        trigger: 'Event 4624 / 4648 hors plage horaire configurée pour le tenant',
        sigmaId: 'win_security_logon_outside_business_hours.yml',
        platforms: [EndpointPlatform.WINDOWS, EndpointPlatform.LINUX],
        mitre: {
            tactics: ['TA0001'],
            techniques: ['T1078'],
        },
        reactionPlan: [
            {
                order: 1,
                instruction: "Contacter l'utilisateur concerné pour confirmer qu'il est bien à l'origine de la connexion.",
            },
            {
                order: 2,
                instruction: "Vérifier l'adresse IP source dans le détail de l'alerte : correspond-elle au réseau interne, au VPN, ou à une IP inconnue ?",
            },
            {
                order: 3,
                instruction: 'Lister les connexions récentes de ce compte pour repérer un schéma inhabituel.',
                command: 'Get-WinEvent -FilterHashtable @{LogName="Security"; ID=4624} -MaxEvents 50 | Where-Object { $_.Message -match "{{user}}" }',
                shell: 'powershell',
            },
            {
                order: 4,
                instruction: "Si l'utilisateur n'est pas à l'origine de la connexion : réinitialiser son mot de passe et invalider ses sessions actives.",
                command: 'net user {{user}} /random /domain',
                shell: 'cmd',
                destructive: true,
            },
            {
                order: 5,
                instruction: 'Clôturer en faux positif si la connexion est légitime, et signaler à MiniSOC si la plage horaire du tenant doit être ajustée.',
            },
        ],
    },
    {
        code: 'RF-004',
        name: 'Brute Force Login',
        nameFr: 'Tentative de force brute',
        description: "Accumulation d'échecs d'authentification (Event 4625) sur une même machine ou un même compte dans une fenêtre courte. Seuil par défaut : 10 échecs en 5 minutes.",
        clientExplanation: "Quelqu'un essaie de devinner un mot de passe en testant de nombreuses combinaisons. Si le compte visé a un mot de passe faible, il finira par tomber.",
        severity: AlertSeverity.HIGH,
        wazuhLevel: 12,
        wazuhRuleIds: ['100204'],
        trigger: '10 × Event 4625 en 5 minutes sur la même machine ou le même compte',
        sigmaId: 'win_security_bruteforce_failed_logons.yml',
        platforms: [EndpointPlatform.WINDOWS, EndpointPlatform.LINUX],
        mitre: {
            tactics: ['TA0006'],
            techniques: ['T1110.001', 'T1110.003'],
        },
        reactionPlan: [
            {
                order: 1,
                instruction: "Identifier l'origine des tentatives dans le détail de l'alerte : IP interne (poste infecté) ou externe (service exposé sur Internet).",
            },
            {
                order: 2,
                instruction: "Vérifier si le compte visé a fini par être compromis (un 4624 après la rafale de 4625).",
                command: 'Get-WinEvent -FilterHashtable @{LogName="Security"; ID=4624; StartTime=(Get-Date).AddHours(-2)}',
                shell: 'powershell',
            },
            {
                order: 3,
                instruction: "Bloquer l'adresse IP source au niveau du pare-feu si elle est externe.",
                command: 'New-NetFirewallRule -DisplayName "MiniSOC bloc {{srcIp}}" -Direction Inbound -RemoteAddress {{srcIp}} -Action Block',
                shell: 'powershell',
                destructive: true,
            },
            {
                order: 4,
                instruction: 'Vérifier que la politique de verrouillage de compte est active (recommandation : 5 échecs, verrouillage 15 minutes).',
                command: 'net accounts',
                shell: 'cmd',
            },
            {
                order: 5,
                instruction: "Si un service d'accès distant (RDP, VPN, webmail) est exposé sans authentification forte, planifier sa mise derrière MFA avec MiniSOC.",
            },
        ],
    },
    {
        code: 'RF-005',
        name: 'Hacker Tool Detected (USB)',
        nameFr: "Outil d'attaque détecté (USB)",
        description: "Chargement d'un pilote ou apparition d'un périphérique correspondant à la signature d'un outil d'attaque physique (Rubber Ducky, Flipper Zero, Bash Bunny, O.MG Cable). Sysmon Event 6 et 1.",
        clientExplanation: "Un matériel conçu pour attaquer les ordinateurs vient d'être branché sur ce poste. C'est un signal d'intrusion physique dans vos locaux, à traiter immédiatement.",
        severity: AlertSeverity.CRITICAL,
        wazuhLevel: 15,
        wazuhRuleIds: ['100205'],
        trigger: 'Sysmon Event 6 / 1 — signature de pilote ou VID:PID connu',
        sigmaId: 'win_sysmon_usb_attack_tool_driver.yml',
        platforms: [EndpointPlatform.WINDOWS, EndpointPlatform.LINUX],
        mitre: {
            tactics: ['TA0001', 'TA0002'],
            techniques: ['T1200', 'T1059'],
        },
        reactionPlan: [
            {
                order: 1,
                instruction: 'Se rendre physiquement sur le poste et débrancher tout périphérique USB non identifié. Conserver le matériel.',
                destructive: true,
            },
            {
                order: 2,
                instruction: 'Identifier qui avait accès à la machine sur le créneau indiqué (badge, caméra, présence).',
            },
            {
                order: 3,
                instruction: 'Lister les processus lancés dans les minutes suivant le branchement.',
                command: 'Get-WinEvent -LogName "Microsoft-Windows-Sysmon/Operational" -MaxEvents 200 | Where-Object Id -eq 1 | Select-Object TimeCreated, Message',
                shell: 'powershell',
            },
            {
                order: 4,
                instruction: 'Isoler le poste du réseau et déclencher un scan antivirus complet.',
                command: 'clamscan -r --infected --log=C:\\MiniSOC\\scan.log C:\\',
                shell: 'cmd',
                destructive: true,
            },
            {
                order: 5,
                instruction: "Réinitialiser les mots de passe des comptes utilisés sur ce poste : un outil de ce type capture les identifiants en quelques secondes.",
                destructive: true,
            },
            {
                order: 6,
                instruction: 'Contacter MiniSOC : cet incident justifie une analyse forensique et probablement un dépôt de plainte.',
            },
        ],
    },
    {
        code: 'RF-006',
        name: 'Malware Detected',
        nameFr: 'Logiciel malveillant détecté',
        description: 'Détection ClamAV lors d’un scan planifié ou à l’accès. Le nom de la signature et le chemin du fichier sont remontés dans les métadonnées de l’alerte.',
        clientExplanation: "L'antivirus a trouvé un fichier malveillant sur cette machine. Il faut vérifier qu'il a bien été mis en quarantaine et comprendre comment il est arrivé là.",
        severity: AlertSeverity.HIGH,
        wazuhLevel: 12,
        wazuhRuleIds: ['100206'],
        trigger: 'Alerte ClamAV (FOUND) remontée par l’agent Wazuh',
        sigmaId: 'clamav_malware_found.yml',
        platforms: [EndpointPlatform.WINDOWS, EndpointPlatform.LINUX],
        mitre: {
            tactics: ['TA0002'],
            techniques: ['T1204', 'T1059'],
        },
        reactionPlan: [
            {
                order: 1,
                instruction: "Vérifier dans le détail de l'alerte que le fichier a bien été mis en quarantaine ou supprimé.",
            },
            {
                order: 2,
                instruction: "Identifier l'origine du fichier (téléchargement, pièce jointe, clé USB, partage réseau) et prévenir l'utilisateur.",
            },
            {
                order: 3,
                instruction: 'Lancer un scan complet de la machine.',
                command: 'clamscan -r --infected --remove=no --log=C:\\MiniSOC\\fullscan.log C:\\',
                shell: 'cmd',
            },
            {
                order: 4,
                instruction: 'Rechercher le même fichier sur le reste du parc via la signature indiquée, notamment sur les partages réseau.',
            },
            {
                order: 5,
                instruction: "Si le fichier provient d'un email, signaler l'expéditeur et vérifier qui d'autre l'a reçu.",
            },
        ],
    },
    {
        code: 'RF-007',
        name: 'Critical Service Stopped',
        nameFr: 'Service de sécurité arrêté',
        description: "Arrêt ou désactivation d'un service de sécurité (antivirus, pare-feu, agent Wazuh) — Events 7036 / 7045 / 7040. Signal classique de contournement des défenses avant la charge utile.",
        clientExplanation: "Une protection de cette machine (antivirus, pare-feu ou agent de surveillance) vient d'être arrêtée. Sans elle, la machine est aveugle et sans défense.",
        severity: AlertSeverity.HIGH,
        wazuhLevel: 12,
        wazuhRuleIds: ['100207'],
        trigger: 'Event 7036 / 7040 / 7045 sur un service de sécurité référencé',
        sigmaId: 'win_system_security_service_stopped.yml',
        platforms: [EndpointPlatform.WINDOWS, EndpointPlatform.LINUX],
        mitre: {
            tactics: ['TA0005', 'TA0040'],
            techniques: ['T1562.001'],
        },
        reactionPlan: [
            {
                order: 1,
                instruction: "Vérifier si une mise à jour ou une intervention planifiée explique l'arrêt du service.",
            },
            {
                order: 2,
                instruction: "Contrôler l'état actuel du service et le redémarrer.",
                command: 'Get-Service -Name "{{serviceName}}" | Start-Service',
                shell: 'powershell',
            },
            {
                order: 3,
                instruction: 'Identifier le compte à l’origine de l’arrêt dans le détail de l’alerte.',
            },
            {
                order: 4,
                instruction: "Si l'arrêt n'est pas légitime : isoler la machine et rechercher ce qui a été exécuté juste après.",
                destructive: true,
            },
            {
                order: 5,
                instruction: 'Vérifier que le service est bien configuré en démarrage automatique pour éviter la récidive.',
                command: 'Set-Service -Name "{{serviceName}}" -StartupType Automatic',
                shell: 'powershell',
            },
        ],
    },
];
const CATALOG_BY_CODE = new Map(RED_FLAG_CATALOG.map((rule) => [rule.code, rule]));
/** Index inverse : ID de règle Wazuh → définition Red Flag. */
const CATALOG_BY_WAZUH_RULE_ID = new Map(RED_FLAG_CATALOG.flatMap((rule) => rule.wazuhRuleIds.map((id) => [id, rule])));
export const findRedFlagByCode = (code) => CATALOG_BY_CODE.get(code.toUpperCase());
export const findRedFlagByWazuhRuleId = (wazuhRuleId) => CATALOG_BY_WAZUH_RULE_ID.get(wazuhRuleId);
/**
 * Substitue les jetons `{{...}}` d'une étape de plan de réaction par les
 * valeurs réelles de l'alerte.
 *
 * Les jetons non résolus sont laissés en place volontairement : mieux vaut
 * afficher `{{srcIp}}` et laisser l'administrateur compléter que produire une
 * commande silencieusement incomplète.
 */
export function renderReactionCommand(command, context) {
    return command.replace(/\{\{(\w+)\}\}/g, (match, key) => context[key] ?? match);
}
//# sourceMappingURL=red-flag-catalog.js.map